import { Badge, Box, Button, Drawer, ScrollArea, Select, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { memo, useEffect, useState } from 'react';
import { IOrderProduct, LineStatus } from '../../interfaces/Product';
import { useUpdateLineStatuses } from '../../hooks/orders/mutations';
import { showNotification } from '../../utils/Notification/Notification';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from '../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../utils/monitoring/Monitoring_Events_Enum';

const LINE_STATUS_TRANSITIONS: Record<string, string[]> = {
  created: ['cleaning', 'cancelled'],
  cleaning: ['ready', 'cancelled'],
  ready: ['collected', 'uncollected', 'cancelled'],
  collected: [],
  uncollected: [],
  cancelled: []
};

export const LINE_STATUS_COLORS: Record<string, string> = {
  created: 'gray',
  cleaning: 'blue',
  ready: 'green',
  collected: 'teal',
  uncollected: 'orange',
  cancelled: 'red'
};

export default memo(function OrderLineStatusModal({
  opened,
  close,
  order,
  orderId
}: {
  opened: boolean;
  close: () => void;
  order: any;
  orderId: string;
}) {
  if (!opened) return;

  const { t } = useTranslation(['OrdersPage']);
  const posthog = usePostHog();
  const { mutateAsync, isPending } = useUpdateLineStatuses();
  const [changes, setChanges] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (opened) {
      setChanges({});
    }
  }, [opened]);

  const lines: IOrderProduct[] = [
    ...(order?.products || []),
    ...(order?.buyAndGetProducts || [])
  ].filter((line: IOrderProduct) => !!line.lineId);

  const getEffectiveStatus = (line: IOrderProduct): LineStatus => line.status || 'created';

  const getTransitionOptions = (currentStatus: LineStatus) => {
    const targets = LINE_STATUS_TRANSITIONS[currentStatus] || [];
    return targets.map((s) => ({
      value: s,
      label: t(`OrdersPage:lineStatusModal.statuses.${s}`)
    }));
  };

  const hasChanges = Object.values(changes).some((v) => v !== null);

  const handleSave = async () => {
    const updates = Object.entries(changes)
      .filter(([, status]) => status !== null)
      .map(([lineId, status]) => ({ lineId, status: status as string }));

    if (!updates.length) return;

    try {
      await mutateAsync({ orderId, updates });
      trackEvent(posthog, MonitoringEventTypes.ORDER_LINE_STATUS_UPDATE_SUCCESS, {
        orderId,
        updatedLines: updates.length,
        statuses: updates.map((u) => u.status)
      });
      close();
      showNotification({
        type: 'Default',
        title: t('OrdersPage:lineStatusModal.successMessage')
      });
    } catch {
      trackEvent(posthog, MonitoringEventTypes.ORDER_LINE_STATUS_UPDATE_FAIL, {
        orderId,
        updatedLines: updates.length
      });
      // Drawer stays open, backendErrorsHandler in mutation handles notification
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      title={t('OrdersPage:lineStatusModal.title')}
      position="right"
      size="lg"
      scrollAreaComponent={ScrollArea.Autosize}>
      <LoaderOverlay visible={isPending} />
      <Text size="sm" c="dimmed" mb="md">
        {t('OrdersPage:lineStatusModal.helperText')}
      </Text>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('OrdersPage:lineStatusModal.product')}</Table.Th>
            <Table.Th>{t('OrdersPage:lineStatusModal.currentStatus')}</Table.Th>
            <Table.Th>{t('OrdersPage:lineStatusModal.newStatus')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {lines.map((line) => {
            const currentStatus = getEffectiveStatus(line);
            const options = getTransitionOptions(currentStatus);
            const isTerminal = options.length === 0;
            return (
              <Table.Tr key={line.lineId}>
                <Table.Td>
                  (x{line.quantity}) {line.name}
                  {line.isBuyAndGetProduct && (
                    <Badge size="xs" ml="xs" variant="light" color="violet">
                      {t('OrdersPage:lineStatusModal.buyAndGet')}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge color={LINE_STATUS_COLORS[currentStatus] || 'gray'} variant="light">
                    {t(`OrdersPage:lineStatusModal.statuses.${currentStatus}`)}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {isTerminal ? (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  ) : (
                    <Select
                      size="xs"
                      placeholder={t('OrdersPage:lineStatusModal.noChange')}
                      data={options}
                      value={changes[line.lineId!] || null}
                      onChange={(value) =>
                        setChanges((prev) => ({
                          ...prev,
                          [line.lineId!]: value
                        }))
                      }
                      clearable
                    />
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <Box
        pt="md"
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--mantine-color-body)',
          borderTop: '1px solid var(--mantine-color-gray-3)'
        }}>
        <Button fullWidth onClick={handleSave} loading={isPending} disabled={!hasChanges}>
          {t('OrdersPage:lineStatusModal.save')}
        </Button>
      </Box>
    </Drawer>
  );
});
